import { AuthApiError, Session } from "@supabase/supabase-js"
import React, { useState } from "react"
import { useAsyncAction } from "../../hooks/useAsyncAction"
import { useClient } from "../../providers/supabase"
import { useNavigate, Link } from "react-router"
import { present } from "../../../lib/util/present"

interface SignInFormProps {
  onLoginSuccess?: (data: {session: Session}) => any
}

interface SignInFormState {
  email: string
}

/** Basic RFC-5322-inspired check that catches obvious non-emails without being overly strict. */
function isValidEmailFormat(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function SignInWithEmailForm({
  onLoginSuccess
}: SignInFormProps) {
  const client = useClient()
  const navigate = useNavigate()
  const [state, setState] = useState<SignInFormState>({
    email: '',
  })
  const [validationError, setValidationError] = useState<string | null>(null)

  const [{loading: submitting, data, error}, submit] = useAsyncAction(async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)
    if (!state.email) { throw new Error(`Email is required`) }

    // Validate format client-side before hitting Supabase.  An invalid email
    // would cause Supabase to return AuthApiError "invalid format", which
    // useAsyncAction forwards to Sentry — creating noise for what is really a
    // user-input mistake (REIMBURSEMENT-TRACKER-WEB-9G).
    if (!isValidEmailFormat(state.email)) {
      setValidationError('Please enter a valid email address.')
      return
    }

    // Deliberately no Sentry.setUser() here: pre-login the only identifier we
    // have is the email address, and that is a HIPAA identifier. A failed login
    // reports without a user; onAuthStateChange sets the user id once we have a
    // session (see providers/supabase.tsx).

    const resp = await client.auth.signInWithOtp({
      email: state.email,
      options: {
        shouldCreateUser: true,
      }
    })
    if (resp.error) {
      throw resp.error
    }

    navigate(`/verifyOtp?email=${state.email}`)
    return resp.data
  }, [onLoginSuccess, state.email])

  const canSubmit = present(state.email)

  return <form onSubmit={submit}>
      <div className="mb-3">
        <span className='form-label'>Email</span>
        <input name="email" type="email"
          className="form-control"
          required
          disabled={submitting}
          value={state.email}
          onChange={(e) => { setState({...state, email: e.target.value }) }}></input>
      </div>

      <div className="mb-3 d-flex">
      {submitting ?
        <button className='btn btn-primary disabled'>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </button> :
        <button type='submit' className='btn btn-primary' disabled={!canSubmit}>Submit</button>}
      </div>

      <div className="mb-3">
        {(validationError || error) &&
          <p className="text-danger">
            {validationError ?? error?.message}
          </p>}
      </div>

  </form>
}
